import { Api } from "./Api";
import _ from "lodash";
import axios from "axios";
import {
    EntretienMateriel,
    EquipementMateriel,
    HistoriqueGroupe,
    MouvementMateriel,
} from "../models/Materiel";

export class MaterielApi extends Api {
    constructor() {
        super("materiel");
    }

    // --- Équipements ---
    getEquipements(params?: Object): Promise<Array<EquipementMateriel>> {
        return axios
            .get(this.baseUrl + "materiel/equipement", { params: params })
            .then(function (response) {
                let res: Array<EquipementMateriel> = [];
                if (response.data && response.data.data) {
                    res = _.map(response.data.data, function (e) {
                        return new EquipementMateriel(e);
                    });
                }
                return res;
            });
    }

    getEquipement(
        id: number,
        params?: Object
    ): Promise<EquipementMateriel> {
        return axios
            .get(this.baseUrl + "materiel/equipement/" + id, {
                params: params,
            })
            .then(function (response) {
                return new EquipementMateriel(response.data.data);
            });
    }

    saveEquipement(
        equipement: EquipementMateriel
    ): Promise<EquipementMateriel> {
        let payload = equipement.serialize();
        let request = equipement.id
            ? axios.put(
                  this.baseUrl + "materiel/equipement/" + equipement.id,
                  payload
              )
            : axios.post(this.baseUrl + "materiel/equipement", payload);
        return request.then(function (response) {
            return new EquipementMateriel(response.data.data);
        });
    }

    deleteEquipement(id: number): Promise<any> {
        return axios.delete(this.baseUrl + "materiel/equipement/" + id);
    }

    archiveEquipement(id: number): Promise<EquipementMateriel> {
        return axios
            .post(this.baseUrl + "materiel/equipement/" + id + "/archive")
            .then(function (response) {
                return new EquipementMateriel(response.data.data);
            });
    }

    unarchiveEquipement(id: number): Promise<EquipementMateriel> {
        return axios
            .post(this.baseUrl + "materiel/equipement/" + id + "/unarchive")
            .then(function (response) {
                return new EquipementMateriel(response.data.data);
            });
    }

    /** Prochain numéro disponible dans une catégorie. */
    getNextNumero(categorieId: number): Promise<number> {
        return axios
            .get(this.baseUrl + "materiel/equipement/next-numero", {
                params: { categorie_id: categorieId },
            })
            .then(function (response) {
                let data = response.data ? response.data.data : null;
                return data && data.numero ? data.numero : 1;
            });
    }

    setEquipementPhoto(
        id: number,
        photoId: number
    ): Promise<EquipementMateriel> {
        return axios
            .post(
                this.baseUrl +
                    "materiel/equipement/" +
                    id +
                    "/photo/" +
                    photoId
            )
            .then(function (response) {
                return new EquipementMateriel(response.data.data);
            });
    }

    removeEquipementPhoto(id: number): Promise<EquipementMateriel> {
        return axios
            .delete(this.baseUrl + "materiel/equipement/" + id + "/photo")
            .then(function (response) {
                return new EquipementMateriel(response.data.data);
            });
    }

    // --- Mouvements ---
    getMouvements(params?: Object): Promise<Api.CallResponse> {
        return axios
            .get(this.baseUrl + "materiel/mouvement", { params: params })
            .then(function (response) {
                let res: Array<MouvementMateriel> = [];
                if (response.data && response.data.data) {
                    res = _.map(response.data.data, function (m) {
                        return new MouvementMateriel(m);
                    });
                }
                return {
                    data: res,
                    metadata: response.data.metadata,
                    paginator: response.data.paginator,
                };
            });
    }

    /** Historique regroupé par équipement, paginé par équipement. */
    getHistorique(params?: Object): Promise<Api.CallResponse> {
        return axios
            .get(this.baseUrl + "materiel/historique", { params: params })
            .then(function (response) {
                let res: Array<HistoriqueGroupe> = [];
                if (response.data && response.data.data) {
                    res = _.map(response.data.data, function (g: any) {
                        return {
                            equipement: new EquipementMateriel(g.equipement),
                            nb_sorties: g.nb_sorties,
                            nb_retours: g.nb_retours,
                            nb_anomalies: g.nb_anomalies,
                            mouvements: _.map(
                                g.mouvements || [],
                                function (m) {
                                    return new MouvementMateriel(m);
                                }
                            ),
                        };
                    });
                }
                return {
                    data: res,
                    metadata: response.data.metadata,
                    paginator: response.data.paginator,
                };
            });
    }

    getMouvement(id: number, params?: Object): Promise<MouvementMateriel> {
        return axios
            .get(this.baseUrl + "materiel/mouvement/" + id, { params: params })
            .then(function (response) {
                return new MouvementMateriel(response.data.data);
            });
    }

    /**
     * Enregistre un lot de mouvements (sortie ou retour).
     * Envoie un en-tête `Idempotency-Key` pour protéger le lot signé contre
     * un double envoi (double-clic, reprise réseau).
     */
    createMouvements(payload: any): Promise<Array<MouvementMateriel>> {
        return axios
            .post(this.baseUrl + "materiel/mouvement", payload, {
                headers: { "Idempotency-Key": MaterielApi.uuidV4() },
            })
            .then(function (response) {
                let res: Array<MouvementMateriel> = [];
                if (response.data && response.data.data) {
                    res = _.map(response.data.data, function (m) {
                        return new MouvementMateriel(m);
                    });
                }
                return res;
            });
    }

    /**
     * `raison` : motif saisi par l'utilisateur, consigné dans la corbeille
     * (cf. core/Modals/ConfirmSuppression).
     */
    deleteMouvement(id: number, raison: string): Promise<any> {
        return axios.delete(this.baseUrl + "materiel/mouvement/" + id, {
            data: { raison: raison },
        });
    }

    addMouvementPhoto(id: number, photoId: number): Promise<any> {
        return axios.post(
            this.baseUrl + "materiel/mouvement/" + id + "/photo/" + photoId
        );
    }

    removeMouvementPhoto(id: number, photoId: number): Promise<any> {
        return axios.delete(
            this.baseUrl + "materiel/mouvement/" + id + "/photo/" + photoId
        );
    }

    // --- Entretiens ---
    getEntretiens(params?: Object): Promise<Array<EntretienMateriel>> {
        return axios
            .get(this.baseUrl + "materiel/entretien", { params: params })
            .then(function (response) {
                let res: Array<EntretienMateriel> = [];
                if (response.data && response.data.data) {
                    res = _.map(response.data.data, function (e) {
                        return new EntretienMateriel(e);
                    });
                }
                return res;
            });
    }

    saveEntretien(entretien: EntretienMateriel): Promise<EntretienMateriel> {
        let payload = entretien.serialize();
        let request = entretien.id
            ? axios.put(
                  this.baseUrl + "materiel/entretien/" + entretien.id,
                  payload
              )
            : axios.post(this.baseUrl + "materiel/entretien", payload);
        return request.then(function (response) {
            return new EntretienMateriel(response.data.data);
        });
    }

    deleteEntretien(id: number): Promise<any> {
        return axios.delete(this.baseUrl + "materiel/entretien/" + id);
    }

    // --- Statistiques ---
    getStats(params?: Object): Promise<any> {
        return axios
            .get(this.baseUrl + "materiel/stats", { params: params })
            .then(function (response) {
                return response.data.data;
            });
    }

    // --- Rapport PDF ---
    rapportPdfUrl(id: number, action: string = "I"): string {
        return this.baseUrl + "materiel/mouvement/" + id + "/pdf/" + action;
    }

    // --- Étiquettes QR (planche PDF) ---
    etiquettesPdfUrl(ids: number[], action: string = "I"): string {
        return (
            this.baseUrl +
            "materiel/equipement/etiquettes/" +
            action +
            "?ids=" +
            ids.join(",")
        );
    }

    /**
     * Ouvre dans un nouvel onglet un endpoint protégé par JWT en passant par un
     * blob (l'en-tête Authorization est porté par axios, pas par window.open).
     */
    openInNewTab(url: string): Promise<void> {
        return axios
            .get(url, { responseType: "blob" })
            .then(function (response) {
                let blobUrl = URL.createObjectURL(response.data);
                window.open(blobUrl, "_blank");
            });
    }

    /** UUID v4, via `crypto.randomUUID()` quand il est disponible. */
    private static uuidV4(): string {
        let cryptoObj: any =
            typeof window !== "undefined" ? (window as any).crypto : null;
        if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
            return cryptoObj.randomUUID();
        }
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
            /[xy]/g,
            function (c) {
                let r = (Math.random() * 16) | 0;
                let v = c === "x" ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            }
        );
    }
}
